
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { KitConfig } from "@/types/ticker";
import { useToast } from "@/hooks/use-toast";

export function useKits() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: kits = [] } = useQuery({
    queryKey: ['kits'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('kits')
        .select('*')
        .order('number', { ascending: true });
      
      if (error) throw error;
      return data as KitConfig[];
    },
  });

  const getKitByNumber = async (number: string) => {
    const { data, error } = await supabase
      .from('kits')
      .select('*')
      .eq('number', parseInt(number, 10))
      .single();
    
    if (error) {
      console.error('Error fetching kit:', error);
      return null;
    }
    return data;
  };

  const updateKitMutation = useMutation({
    mutationFn: async (config: KitConfig) => {
      console.log('Updating kit with:', config);
      const { data, error } = await supabase
        .from('kits')
        .update({
          speed: config.speed,
          background_color: config.background_color,
          text_color: config.text_color,
          name: config.name,
          number: config.number
        })
        .eq('id', config.id)
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['kits'] });
      toast({
        title: "Changes saved",
        description: "Kit configuration has been updated successfully.",
        className: "bg-[#1A1F2C] border border-white/10 text-white",
      });
    },
    onError: (error) => {
      console.error('Error updating kit:', error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
        className: "bg-red-950 border border-red-900 text-white",
      });
    },
  });

  return { kits, updateKitMutation, getKitByNumber };
}
